import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { RefreshButton } from "@/components/RefreshButton";
import { ArrowLeft, Camera, Check, House, Images, PencilSimple, Trash, VideoCamera } from "@phosphor-icons/react";
import { api, adminHeaders, fileUrl, getSecret, clearSecret } from "@/lib/api";
import { compressImage } from "@/lib/compress";
import { Lightbox } from "@/components/Lightbox";

const PAGE_SIZE = 5;

const fmtDate = (d) =>
    new Date(d + "T00:00:00Z").toLocaleDateString("id-ID", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "Asia/Jakarta",
    });

const srcLabel = (s) => (s === "admin" ? "Admin" : s === "cctv" ? "CCTV" : s === "wa" ? "WhatsApp" : "Supervisor");

const nowWibHHMM = () => {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit", hour12: false }).format(
        new Date()
    );
    return parts;
};

export default function ProgressPage({ mode = "supervisor" }) {
    const { roomId } = useParams();
    const navigate = useNavigate();
    const [room, setRoom] = useState(null);
    const [groups, setGroups] = useState([]);
    const [totalDates, setTotalDates] = useState(0);
    const [totalPhotos, setTotalPhotos] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [adminVerified, setAdminVerified] = useState(mode !== "admin");
    const [note, setNote] = useState("");
    const [uploading, setUploading] = useState(false);
    const [editId, setEditId] = useState("");
    const [editNote, setEditNote] = useState("");
    const [lightbox, setLightbox] = useState(null);
    const camRef = useRef(null);
    const galRef = useRef(null);
    const [cctvSerial, setCctvSerial] = useState("");
    const [cctvCameras, setCctvCameras] = useState([]);
    const [cctvMsg, setCctvMsg] = useState("");
    const [cctvBusy, setCctvBusy] = useState("");
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyDate, setHistoryDate] = useState("");
    const isAdmin = mode === "admin" && adminVerified;
    const backPath = mode === "admin" ? `/a/${roomId}` : `/s/${roomId}`;

    useEffect(() => {
        if (mode !== "admin") return;
        const saved = getSecret();
        if (!saved) {
            navigate("/admin");
            return;
        }
        api.post("/admin/verify", { phrase: saved })
            .then(() => setAdminVerified(true))
            .catch(() => {
                clearSecret();
                navigate("/admin");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const fetchBatch = useCallback(
        async (offset, append) => {
            const res = await api.get(`/rooms/${roomId}/progress`, { params: { date_offset: offset, date_limit: PAGE_SIZE } });
            setTotalDates(res.data.total_dates);
            setTotalPhotos(res.data.total_photos);
            setGroups((prev) => (append ? [...prev, ...res.data.groups] : res.data.groups));
        },
        [roomId]
    );

    useEffect(() => {
        if (mode === "admin" && !adminVerified) return;
        api.get(`/rooms/${roomId}`)
            .then((r) => setRoom(r.data))
            .catch(() => setRoom(null));
        fetchBatch(0, false)
            .catch(() => toast.error("Gagal memuat foto progress"))
            .finally(() => setLoading(false));
        if (mode === "admin") loadCctv();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomId, adminVerified]);

    const loadCctv = async () => {
        try {
            const r = await api.get(`/admin/rooms/${roomId}/cctv`, { headers: adminHeaders() });
            setCctvSerial(r.data.device_serial || "");
        } catch {
            /* abaikan */
        }
        try {
            const r = await api.get("/admin/cctv/cameras", { headers: adminHeaders() });
            setCctvCameras(r.data.cameras);
        } catch (err) {
            setCctvMsg(err?.response?.data?.detail || "Daftar kamera tidak tersedia");
        }
    };

    const saveCctv = async (serial) => {
        setCctvBusy("save");
        try {
            await api.put(`/admin/rooms/${roomId}/cctv`, { device_serial: serial }, { headers: adminHeaders() });
            setCctvSerial(serial);
            toast.success(serial ? "Kamera dipasang ke ruangan ini" : "Kamera dilepas");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Gagal menyimpan kamera");
        } finally {
            setCctvBusy("");
        }
    };

    const searchHistory = async () => {
        if (!historyDate) return;
        setCctvBusy("history");
        try {
            const res = await api.post(
                "/admin/cctv/history-capture",
                { room_id: roomId, date: historyDate },
                { headers: adminHeaders() }
            );
            const results = res.data.results || {};
            const okCount = Object.values(results).filter((r) => r === "ok").length;
            const missed = Object.entries(results)
                .filter(([, r]) => r !== "ok")
                .map(([t]) => t);
            if (okCount > 0) {
                toast.success(`${okCount} jadwal ditemukan & disimpan ke progress`);
                await reload();
            }
            if (missed.length) toast.warning(`Tidak ada gambar untuk jam: ${missed.join(", ")}`);
            setHistoryOpen(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Gagal mencari history");
        } finally {
            setCctvBusy("");
        }
    };

    const testCctv = async () => {
        setCctvBusy("test");
        try {
            const res = await api.post("/admin/cctv/test-capture", { room_id: roomId, target: nowWibHHMM() }, { headers: adminHeaders() });
            if (res.data.queued) {
                toast.info("Perintah diteruskan ke kamera — bila berhasil, foto muncul di galeri dalam ±1 menit");
                setTimeout(() => reload(), 35000);
                setTimeout(() => reload(), 80000);
            } else if (res.data.result === "ok" || res.data.result === "ok_recording" || res.data.result === "ok_event") {
                toast.success(res.data.result === "ok_recording" ? "Jepretan diambil dari rekaman (kamera offline)" : "Jepretan CCTV tersimpan");
                await reload();
            } else if (res.data.result === "offline") {
                toast.warning("Kamera sedang offline — tidak ada foto diambil");
            } else if (res.data.result === "no_footage") {
                toast.warning("Tidak ada footage dalam 1 jam terakhir — tidak ada foto diambil");
            } else {
                toast.info(`Hasil: ${res.data.result}`);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Gagal menjepret CCTV");
        } finally {
            setCctvBusy("");
        }
    };

    const loadMore = async () => {
        setLoadingMore(true);
        try {
            await fetchBatch(groups.length, true);
        } catch {
            toast.error("Gagal memuat tanggal berikutnya");
        } finally {
            setLoadingMore(false);
        }
    };

    const reload = async () => {
        try {
            await fetchBatch(0, false);
        } catch {
            toast.error("Gagal memuat ulang");
        }
    };

    const upload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            const form = new FormData();
            for (const file of files) {
                const compressed = await compressImage(file);
                form.append("files", compressed, "progress.jpg");
            }
            form.append("note", note);
            form.append("source", mode === "admin" ? "admin" : "supervisor");
            await api.post(`/rooms/${roomId}/progress`, form);
            setNote("");
            toast.success(`${files.length} foto progress terunggah`);
            await reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Gagal mengunggah foto progress");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const saveNote = async (photo) => {
        try {
            await api.patch(`/rooms/${roomId}/progress/${photo.id}`, { note: editNote });
            setEditId("");
            toast.success("Catatan disimpan");
            await reload();
        } catch {
            toast.error("Gagal menyimpan catatan");
        }
    };

    const deletePhoto = async (photo) => {
        if (!window.confirm("Hapus foto progress ini?")) return;
        try {
            await api.delete(`/admin/progress-photos/${photo.id}`, { headers: adminHeaders() });
            toast.success("Foto progress dihapus");
            await reload();
        } catch {
            toast.error("Gagal menghapus foto progress");
        }
    };

    if (mode === "admin" && !adminVerified) return null;

    return (
        <div className="min-h-screen flex flex-col" data-testid="progress-page">
            <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-4xl mx-auto w-full px-6 py-4 flex items-center justify-between gap-4">
                    <button
                        data-testid="progress-back"
                        onClick={() => navigate(backPath)}
                        className="text-muted-foreground hover:text-primary transition-colors"
                        title="Kembali ke ruangan"
                    >
                        <ArrowLeft size={20} weight="light" />
                    </button>
                    <div className="text-center min-w-0">
                        <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-muted-foreground truncate" data-testid="progress-room-name">
                            {room ? `${room.property_name} — ${room.name}` : "Memuat..."}
                        </p>
                        <h1 className="font-serif text-2xl sm:text-3xl tracking-tight font-medium">Foto Progress Lapangan</h1>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <RefreshButton />
                        <button
                            data-testid="progress-home-btn"
                            onClick={() => navigate(mode === "admin" ? "/admin" : "/supervisor")}
                            className="text-muted-foreground hover:text-primary transition-colors shrink-0"
                            title="Kembali ke pemindai"
                        >
                            <House size={20} weight="light" />
                        </button>
                    </div>
                </div>
            </header>

            <main className="flex-1 w-full max-w-4xl mx-auto px-6 py-6 space-y-8">
                {isAdmin && (
                    <div className="border border-border bg-white p-4" data-testid="cctv-picker">
                        <div className="flex items-center gap-2 mb-1">
                            <VideoCamera size={18} weight="light" className="text-primary" />
                            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Kamera CCTV</p>
                        </div>
                        <p className="text-xs text-muted-foreground mb-3">Satu kamera hanya bisa untuk satu ruangan. Daftar kamera diambil dari server EZVIZ.</p>
                        <div className="flex flex-wrap gap-2">
                            <select
                                data-testid="cctv-camera-select"
                                value={cctvSerial}
                                onChange={(e) => setCctvSerial(e.target.value)}
                                className="flex-1 min-w-[200px] border border-border px-3 py-2 text-sm outline-none focus:border-primary bg-white"
                            >
                                <option value="">— Pilih kamera —</option>
                                {cctvCameras.map((c) => (
                                    <option key={c.serial} value={c.serial} disabled={!!c.assigned_to && c.assigned_to.room_id !== roomId}>
                                        {c.name}
                                        {c.status === 1 ? " (online)" : " (offline)"}
                                        {c.assigned_to && c.assigned_to.room_id !== roomId ? ` — dipakai ${c.assigned_to.room_name}` : ""}
                                    </option>
                                ))}
                            </select>
                            <button
                                data-testid="cctv-assign-btn"
                                onClick={() => saveCctv(cctvSerial.trim())}
                                disabled={cctvBusy === "save"}
                                className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-[#9c4a2a] transition-colors disabled:opacity-50"
                            >
                                Simpan
                            </button>
                            {cctvSerial && (
                                <button
                                    data-testid="cctv-unassign-btn"
                                    onClick={() => saveCctv("")}
                                    disabled={cctvBusy === "save"}
                                    className="border border-border px-4 py-2 text-sm hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
                                >
                                    Lepas
                                </button>
                            )}
                            <button
                                data-testid="cctv-test-btn"
                                onClick={testCctv}
                                disabled={cctvBusy === "test" || !cctvSerial}
                                className="border border-border px-4 py-2 text-sm hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
                            >
                                {cctvBusy === "test" ? "Menjepret..." : "Tes Jepret"}
                            </button>
                            <button
                                data-testid="cctv-history-btn"
                                onClick={() => setHistoryOpen((v) => !v)}
                                disabled={!cctvSerial}
                                className="hidden sm:inline-flex border border-border px-4 py-2 text-sm hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
                                title="Cari rekaman tanggal lampau (khusus desktop)"
                            >
                                Cari History
                            </button>
                        </div>
                        {historyOpen && (
                            <div className="hidden sm:flex items-center gap-2 mt-3" data-testid="cctv-history-form">
                                <input
                                    type="date"
                                    data-testid="cctv-history-date"
                                    value={historyDate}
                                    max={new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" })}
                                    onChange={(e) => setHistoryDate(e.target.value)}
                                    className="border border-border px-3 py-2 text-sm outline-none focus:border-primary bg-transparent"
                                />
                                <button
                                    data-testid="cctv-history-search-btn"
                                    onClick={searchHistory}
                                    disabled={cctvBusy === "history" || !historyDate}
                                    className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-[#9c4a2a] transition-colors disabled:opacity-50"
                                >
                                    {cctvBusy === "history" ? "Mencari..." : "Cari"}
                                </button>
                                <p className="text-xs text-muted-foreground">Mencari rekaman pada jam jadwal (±1 jam) di tanggal terpilih</p>
                            </div>
                        )}
                        {cctvMsg && (
                            <p className="text-xs text-amber-700 mt-2" data-testid="cctv-picker-msg">{cctvMsg}</p>
                        )}
                    </div>
                )}

                <div className="border border-border bg-white p-4" data-testid="progress-upload-box">
                    <input
                        data-testid="progress-note-input"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Catatan (opsional), misal: pemasangan plafon 50%"
                        className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-primary transition-colors bg-transparent mb-3"
                    />
                    <input ref={camRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={upload} data-testid="progress-camera-input" />
                    <input ref={galRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="progress-gallery-input" />
                    <div className="flex gap-2">
                        <button
                            data-testid="progress-upload-camera-btn"
                            onClick={() => camRef.current?.click()}
                            disabled={uploading}
                            className="flex-1 bg-primary text-primary-foreground py-3 text-sm font-medium hover:bg-[#9c4a2a] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            <Camera size={18} weight="bold" />
                            {uploading ? "Mengunggah..." : "Dari Kamera"}
                        </button>
                        <button
                            data-testid="progress-upload-gallery-btn"
                            onClick={() => galRef.current?.click()}
                            disabled={uploading}
                            className="flex-1 border border-primary text-primary py-3 text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            <Images size={18} weight="bold" />
                            {uploading ? "Mengunggah..." : "Dari Galeri"}
                        </button>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2">Bisa pilih banyak foto sekaligus. Foto otomatis dikompresi sebelum diunggah.</p>
                </div>

                {loading ? (
                    <p className="text-sm text-muted-foreground" data-testid="progress-loading">Memuat...</p>
                ) : groups.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-16" data-testid="progress-empty">
                        Belum ada foto progress untuk ruangan ini.
                    </p>
                ) : (
                    <>
                        <p className="text-xs text-muted-foreground" data-testid="progress-summary">
                            {totalPhotos} foto • {totalDates} tanggal
                        </p>
                        {groups.map((g) => (
                            <section key={g.date} data-testid={`progress-group-${g.date}`}>
                                <div className="flex items-baseline gap-3 mb-3">
                                    <h2 className="font-serif text-xl sm:text-2xl tracking-tight font-medium" data-testid={`progress-date-${g.date}`}>
                                        {fmtDate(g.date)}
                                    </h2>
                                    <span className="text-xs text-muted-foreground" data-testid={`progress-count-${g.date}`}>
                                        {g.photos.length} foto
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid={`progress-grid-${g.date}`}>
                                    {g.photos.map((p, i) => (
                                        <div key={p.id} className="border border-border bg-white" data-testid={`progress-photo-${p.id}`}>
                                            <button
                                                className="block w-full aspect-[4/3] bg-secondary overflow-hidden cursor-zoom-in"
                                                onClick={() => setLightbox({ photos: g.photos, date: g.date, index: i })}
                                                data-testid={`progress-zoom-${p.id}`}
                                                aria-label="Perbesar foto progress"
                                            >
                                                <img
                                                    src={fileUrl(p.storage_path)}
                                                    alt={p.note || `Foto progress ${i + 1}`}
                                                    className="w-full h-full object-cover"
                                                    loading="lazy"
                                                />
                                            </button>
                                            <div className="p-2.5">
                                                {editId === p.id ? (
                                                    <div className="flex gap-1">
                                                        <input
                                                            data-testid={`progress-note-edit-${p.id}`}
                                                            value={editNote}
                                                            onChange={(e) => setEditNote(e.target.value)}
                                                            placeholder="Catatan"
                                                            className="flex-1 min-w-0 border border-border px-2 py-1 text-xs outline-none focus:border-primary bg-transparent"
                                                        />
                                                        <button
                                                            data-testid={`progress-note-save-${p.id}`}
                                                            onClick={() => saveNote(p)}
                                                            className="shrink-0 border border-primary text-primary px-2 hover:bg-primary hover:text-primary-foreground transition-colors"
                                                            title="Simpan catatan"
                                                        >
                                                            <Check size={13} weight="bold" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-start gap-1">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs truncate" data-testid={`progress-note-${p.id}`}>{p.note || "Tanpa catatan"}</p>
                                                            <p className="text-[10px] text-muted-foreground font-mono" data-testid={`progress-meta-${p.id}`}>
                                                                {new Date(p.created_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })} • {srcLabel(p.source)}
                                                            </p>
                                                        </div>
                                                        <button
                                                            data-testid={`progress-edit-${p.id}`}
                                                            onClick={() => {
                                                                setEditId(p.id);
                                                                setEditNote(p.note || "");
                                                            }}
                                                            className="shrink-0 text-muted-foreground hover:text-primary transition-colors p-0.5"
                                                            title="Edit catatan"
                                                        >
                                                            <PencilSimple size={14} weight="light" />
                                                        </button>
                                                        {isAdmin && (
                                                            <button
                                                                data-testid={`progress-delete-${p.id}`}
                                                                onClick={() => deletePhoto(p)}
                                                                className="shrink-0 text-muted-foreground hover:text-destructive transition-colors p-0.5"
                                                                title="Hapus foto progress"
                                                            >
                                                                <Trash size={14} weight="light" />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ))}

                        {groups.length < totalDates && (
                            <button
                                data-testid="progress-load-more"
                                onClick={loadMore}
                                disabled={loadingMore}
                                className="w-full border border-primary text-primary py-3 text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-50"
                            >
                                {loadingMore ? "Memuat..." : `Muat ${Math.min(PAGE_SIZE, totalDates - groups.length)} tanggal berikutnya (${totalDates - groups.length} tersisa)`}
                            </button>
                        )}
                    </>
                )}
            </main>

            {lightbox && (
                <Lightbox
                    src={fileUrl(lightbox.photos[lightbox.index].storage_path)}
                    alt={lightbox.photos[lightbox.index].note || "Foto progress"}
                    onClose={() => setLightbox(null)}
                    onPrev={lightbox.photos.length > 1 ? () => setLightbox((s) => ({ ...s, index: (s.index - 1 + s.photos.length) % s.photos.length })) : null}
                    onNext={lightbox.photos.length > 1 ? () => setLightbox((s) => ({ ...s, index: (s.index + 1) % s.photos.length })) : null}
                    caption={
                        <div className="bg-black/60 text-white px-4 py-2.5 text-center space-y-1" data-testid="progress-lightbox-caption">
                            <p className="text-[11px] font-mono tracking-[0.2em] uppercase" data-testid="progress-lightbox-status">
                                {fmtDate(lightbox.date)} • {new Date(lightbox.photos[lightbox.index].created_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })} • {srcLabel(lightbox.photos[lightbox.index].source)}
                            </p>
                            {lightbox.photos[lightbox.index].note && (
                                <p className="text-sm" data-testid="progress-lightbox-note">
                                    {lightbox.photos[lightbox.index].note}
                                </p>
                            )}
                        </div>
                    }
                />
            )}
        </div>
    );
}
